import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";

const EASE = [0.22, 1, 0.36, 1];

/**
 * PageShell — shared shell for internal institutional pages.
 * Keeps the same Nav and Footer as the landing to preserve continuity.
 */
export default function PageShell({ kicker, title, lead, children }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="relative min-h-screen bg-white">
      <Nav />
      <section className="relative env-white pt-[92px]">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 90% 20%, rgba(0,194,209,0.10) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 10% 80%, rgba(46,229,157,0.08) 0%, transparent 60%)",
          }}
        />
        <div className="relative z-10 max-w-[1080px] mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-10">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[13px] text-[#1A3A61]/60 hover:text-[#1A3A61] font-medium mb-8"
            >
              <ArrowLeft size={14} /> Voltar
            </Link>
          </motion.div>

          {kicker && (
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
              className="kicker mb-5"
            >
              {kicker}
            </motion.p>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="font-display text-[#1A3A61] text-[40px] sm:text-[52px] md:text-[64px] leading-[0.98] tracking-[-0.02em] max-w-3xl"
          >
            {title}
          </motion.h1>
          {lead && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.18 }}
              className="mt-6 text-[#1A3A61]/70 text-lg md:text-xl leading-relaxed max-w-2xl"
            >
              {lead}
            </motion.p>
          )}
        </div>
      </section>

      <section className="relative env-white pb-24 md:pb-32">
        <div className="max-w-[1080px] mx-auto px-6 md:px-8">{children}</div>
      </section>

      <Footer />
    </main>
  );
}
